import { Observable, ReplaySubject, Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { PhxDataSourceConfig, PhxDataSourceLoadResult, PhxDataSourcePaginatedResponse, PhxDataSourceStream, PhxPagination, PhxSortParam } from "./types";

export class PhxDataSource<T> {
  /**  a function which takes params and generates a source observable which returns your data */
  public source: (params: Map<string, any>) => Observable<PhxDataSourceLoadResult<T>>;
  /**  a stream on which the resultant data is emitted */
  public data$: ReplaySubject<T> = new ReplaySubject<T>(1);
  /**  the last resultant data which was emitted by the source */
  public data: T;
  /**  a stream on which errors are emitted if there were any while loading the source */
  public error$: ReplaySubject<any> = new ReplaySubject<any>(1);
  /**  the last error which was emitted by the source */
  public error: any;
  /**  a stream on which a change in loading state is emitted */
  public loading$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  /**  whether a load is currently underway */
  public loading: boolean = false;
  public pagination: PhxPagination;
  public sort: PhxSortParam = null;

  private config: Required<PhxDataSourceConfig>;
  private params: Map<string, any>;
  private lastCall: Subscription;
  private lastCallInProgress: boolean = false;

  constructor(source: (params: Map<string, any>) => Observable<PhxDataSourceLoadResult<T>>, config?: PhxDataSourceConfig) {
    this.source = source;
    this.config = {
      sortFieldParam: config?.sortFieldParam ?? 'sort_field',
      sortDirectionParam: config?.sortDirectionParam ?? 'sort_direction',
      pageParam: config?.pageParam ?? 'page_no',
      perPageParam: config?.perPageParam ?? 'per_page',
    };
  }

  public load(params: Map<string, any> = new Map<string, any>()): void {
    if (!this.source) {
      console.error('PhxDataSource: Tried to call load without a defined source function.');
      return;
    }

    this.setParams(params);

    // Cancel the last call if there is one in progress
    if (this.lastCallInProgress && this.lastCall) {
      this.lastCall.unsubscribe();
    }

    this.setLoadingState(true);
    this.lastCall = this.source(this.params)
      .pipe(
        finalize(() => {
          this.setLoadingState(false);
        }),
      )
      .subscribe({
        next: this.emitAndSetData.bind(this),
        error: this.emitAndSetError.bind(this),
      });
  }

  /**
   * Use this to hook into these states from your source.
   *
   * @returns Data, loading and error streams which you can subscribe to, to be updated of these objects.
   */
  public getStreams(): PhxDataSourceStream {
    return {
      data: this.data$,
      loading: this.loading$,
      error: this.error$,
    };
  }

  /** Navigate to the next page. In append mode, data is concatenated. */
  public getNextPage(load: boolean = true): void {
    if (!this.hasNextPage) return;

    this.params.set(this.config.pageParam, this.pagination.pageNo + 1);
    if (load) this.load(this.params);
  }

  /** Whether there are more pages available beyond the current page. */
  public get hasNextPage(): boolean {
    if (!this.pagination) return false;
    return this.pagination.totalItems != null
      ? this.pagination.noOfPages > this.pagination.pageNo
      : this.pagination.totalItemsInLastResponse === this.pagination.perPage;
  }

  /** Set sort params and optionally reload from page 1. */
  public setSort(sort: PhxSortParam, reload: boolean = true): void {
    this.sort = sort;

    if (!this.params) this.params = new Map();

    if (sort) {
      this.params.set(this.config.sortFieldParam, sort.key);
      this.params.set(this.config.sortDirectionParam, sort.direction);
    } else {
      this.params.delete(this.config.sortFieldParam);
      this.params.delete(this.config.sortDirectionParam);
    }

    // Reset to page 1 when sort changes
    this.params.set(this.config.pageParam, 1);

    if (reload) this.load(this.params);
  }

  /** Navigate to a specific page. */
  public setPage(pageNo: number, reload: boolean = true): void {
    if (!this.params) this.params = new Map();
    this.params.set(this.config.pageParam, pageNo);

    if (reload) this.load(this.params);
  }

  /** Set items per page and reset to page 1. */
  public setPerPage(perPage: number, reload: boolean = true): void {
    if (!this.params) this.params = new Map();
    this.params.set(this.config.perPageParam, perPage);
    this.params.set(this.config.pageParam, 1);

    if (reload) this.load(this.params);
  }

  /** Replace the source function (e.g. for conditional API endpoints). */
  public setSource(source: (params: Map<string, any>) => Observable<PhxDataSourceLoadResult<T>>): void {
    this.source = source;
    this.pagination = null;
  }

  public setParams(params: Map<string, any>): void {
    this.params = params || this.params;
  }

  /** Clean up subscriptions. Call this in ngOnDestroy. */
  public destroy(): void {
    if (this.lastCallInProgress && this.lastCall) {
      this.lastCall.unsubscribe();
    }
    this.data$.complete();
    this.loading$.complete();
    this.error$.complete();
  }

  private setLoadingState(isLoading: boolean): void {
    this.loading = isLoading;
    this.loading$.next(isLoading);
    this.lastCallInProgress = isLoading;
  }

  private emitAndSetData(data: PhxDataSourceLoadResult<T>): void {
    const paginatedResponse = isPaginatedResponse(data) ? data : null;
    const items = paginatedResponse?.items ?? data;
    this.pagination = paginatedResponse?.pagination;
    this.data = items as T;
    this.data$.next(this.data);
  }

  private emitAndSetError(error: any): void {
    this.error = error;
    this.error$.next(error);
  }
}

function isPaginatedResponse(data: unknown): data is PhxDataSourcePaginatedResponse<unknown> {
  return !!data && typeof data === 'object' && !Array.isArray(data) && 'items' in data;
}
